import { useState } from "react";
import {
  BYTES_PER_MB,
  DEFAULT_MAX_FILE_SIZE_MB,
  DEFAULT_MAX_FILES,
} from "@/constants";
import toast from "react-hot-toast";
import { useQuizContext } from "@/pages/quiz/context";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { uploadDocument } from "@/api/quiz";
import NewDropzoneUploader from "@/components/common/newDropzoneUploader";
import { getAttachmentDropzoneKind } from "@/utils/attachmentAccept";
import CustomWebcam from "@/components/common/customWebcam";
import { LuLoader } from "react-icons/lu";
import { Button } from "@/pages/quiz/15000/components/Button";
import { FileAnswerType, TemporaryFile } from "@/types";
import { getMaxFilesExceededToastMessage } from "@/pages/quiz/base/common";

type QuestionValue = File[] | null;

const normalizeFiles = (v: unknown): File[] => {
  if (!v || v === "true") return [];
  return v as File[];
};

const AttachmentBlock = (props: QuizQuestionProps<QuestionValue>) => {
  const {
    question: {
      answers,
      id,
      content_type,
      attributes: {
        type = "image",
        answerType,
        maxFiles = DEFAULT_MAX_FILES,
        maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
        required = true,
      },
    },
    questionValue,
    setQuestionValue,
    setQuestionAnswer,
    uploadUserId = "",
  } = props;

  const { voucherId, quizId } = useQuizContext();
  const [temporaryFiles, setTemporaryFiles] = useState<TemporaryFile[]>(
    answers[0]?.temporary_files || []
  );
  const [isLoading, setIsLoading] = useState(false);

  const hasServerAnswer = Boolean(temporaryFiles.length);

  const dropzoneType = getAttachmentDropzoneKind(answerType, type);

  const onChangeQuestionValue = (value: File[]) => {
    setTemporaryFiles([]);
    setQuestionValue(value);
  };

  const handleActionBtn = async () => {
    if (hasServerAnswer) {
      setQuestionAnswer(questionValue, false, true);
      return;
    }
    try {
      setIsLoading(true);

      const willAttachFile = !!(
        typeof questionValue !== "string" && (questionValue as File[])?.length
      );

      if (temporaryFiles.length && !willAttachFile) {
        setQuestionAnswer(questionValue, false, true);
        return;
      }

      if (required && !willAttachFile) return;

      if (willAttachFile) {
        await uploadDocument({
          files: hasServerAnswer ? [] : (questionValue as File[]) || [],
          type: (content_type as FileAnswerType) || "photo",
          userId: uploadUserId,
          isAnonymous: !voucherId,
          questionId: id,
          quizId: quizId || "",
        });
        setQuestionAnswer(questionValue, false, true);
      } else {
        setQuestionAnswer(null, false, false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleWebcamChange = (files: File[], action: "add" | "remove") => {
    if (action !== "add") return;

    setTemporaryFiles([]);

    const current = normalizeFiles(questionValue);

    const validBySize = files.filter((file) => {
      const fileSizeMB = file.size / BYTES_PER_MB;
      if (fileSizeMB > maxFileSizeMB) {
        toast.error(
          `${file.name} exceeds the size limit of ${maxFileSizeMB} MB.`
        );
        return false;
      }
      return true;
    });

    if (!validBySize.length) return;

    const remaining = maxFiles - current.length;
    const filesToAdd = remaining > 0 ? files.slice(0, remaining) : [];

    if (files.length > filesToAdd.length) {
      const toastMessage = getMaxFilesExceededToastMessage({
        maxFiles,
        answerType,
        maxFileSizeMB,
      });

      toast.error(toastMessage);
    }

    if (!filesToAdd.length) {
      setIsLoading(false);
      return;
    }

    setQuestionValue([...current, ...files.slice(0, remaining)]);
  };

  return (
    <div>
      <div className="flex gap-2.5 sm:gap-3 lg:gap-4">
        <div className="flex-1">
          <NewDropzoneUploader
            value={questionValue || []}
            type={dropzoneType}
            maxFiles={maxFiles}
            answerType={answerType}
            maxFileSizeMB={maxFileSizeMB}
            onChange={onChangeQuestionValue}
            temporaryFiles={temporaryFiles}
          />
        </div>
        <div className="flex-1 hidden lg:block">
          <CustomWebcam type={type as any} onChange={handleWebcamChange} />
        </div>
      </div>
      {isLoading ? (
        <LuLoader className="w-7 h-7 mx-auto mt-5 text-blue animate-spin" />
      ) : (
        <Button
          className="mt-2"
          disabled={
            required &&
            (!questionValue ||
              isLoading ||
              (!(questionValue as File[])?.length && !hasServerAnswer))
          }
          onClick={handleActionBtn}
        />
      )}
    </div>
  );
};

export { AttachmentBlock };
